import { useTranslation } from "react-i18next";
import { Drawer } from "@/components/ui/Drawer";
import { useGetSidebarPersmissionsQuery } from "@/services/(my-space)/userApi";
import { useMemo, type Dispatch, type SetStateAction } from "react";
import { SectionTitle } from "@/components/ui/SectionTitle";
import { useSplitScreenContext } from "./split-screen-context";
import { cn } from "@/utils/cn";
import { motion, type Variants } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { ISidebarLink } from "@/types/(company)/roles.interface";
import { NavIcon } from "@/components/layout/sidebar/nav-icon";
import { SPLIT_SCREEN_EXCLUDED_URLS } from "@/routes/split-screen-routes";
import {
  getSidebarDisplayName,
  sortSidebarLinks,
  type SidebarSpace,
} from "@/utils/sidebar";

interface Props {
  isOpen: boolean;
  toggle: Dispatch<SetStateAction<boolean>>;
}

export const SplitModulePicker = ({ isOpen, toggle }: Props) => {
  const { t } = useTranslation(["common", "company"]);
  const { highlightColor, splitPath } = useSplitScreenContext();

  const { data: sidebarLinks } = useGetSidebarPersmissionsQuery();
  // Full-page modules (main Settings) cannot open in the split pane.
  const splittable = (link: ISidebarLink) =>
    !SPLIT_SCREEN_EXCLUDED_URLS.has(`/${link.key}`);
  const sortedMySpaceLinks = sortSidebarLinks(
    sidebarLinks?.data?.individual || [],
    "myspace",
  ).filter(splittable);
  const sortedCompanyLinks = sortSidebarLinks(
    sidebarLinks?.data?.company || [],
    "company",
  ).filter(splittable);

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.03 } },
  };
  const itemVariants: Variants = {
    hidden: { opacity: 0, x: -10 },
    show: { opacity: 1, x: 0 },
  };

  enum TabKeys {
    myspace = "myspace",
    company = "company",
  }

  interface Tab {
    key: TabKeys;
    space: SidebarSpace;
    label: string;
    links: ISidebarLink[];
  }
  const tabs: Tab[] = [
    {
      key: TabKeys.myspace,
      space: "myspace",
      label: "My Space",
      links: sortedMySpaceLinks,
    },
    sortedCompanyLinks?.length
      ? {
          key: TabKeys.company,
          space: "company",
          label: t("company:shared.company"),
          links: sortedCompanyLinks,
        }
      : null,
  ].filter(Boolean) as Tab[];

  const defaultTab = useMemo(() => {
    return splitPath?.startsWith("/company")
      ? TabKeys.company
      : TabKeys.myspace;
  }, [splitPath]);

  return (
    <Drawer hideCloseButton isOpen={isOpen} onClose={() => toggle(false)}>
      <div className="h-full flex flex-col gap-2 overflow-hidden">
        <SectionTitle
          size="sm"
          noMarginBottom
          title={t("common:a11y.module_launcher")}
          lead="Choose a module for bottom panel"
        />

        <Tabs
          defaultValue={defaultTab}
          className="flex-1 flex flex-col min-h-0"
        >
          <TabsList className={cn("grid w-full", `grid-cols-${tabs.length}`)}>
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.key}
                value={tab.key}
                className="rounded-sxs font-semibold border bg-primary/10 text-black data-[state='active']:bg-(--hover-bg)! data-[state='active']:text-white!"
                style={
                  {
                    "--hover-bg": highlightColor,
                  } as React.CSSProperties
                }
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((tab) => (
            <TabsContent
              key={tab.key}
              value={tab.key}
              className="flex-1 min-h-0 mt-4"
            >
              <motion.ul
                animate="show"
                initial="hidden"
                variants={containerVariants}
                className="h-full overflow-y-auto flex flex-col gap-2 pe-2"
              >
                {tab.links.map((link) => (
                  <motion.li key={link.key} variants={itemVariants}>
                    <LinkItem
                      link={link}
                      space={tab.space}
                      toggleDrawer={toggle}
                    />
                  </motion.li>
                ))}
              </motion.ul>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </Drawer>
  );
};

const LinkItem = ({
  link,
  space,
  toggleDrawer,
}: {
  link: ISidebarLink;
  space: SidebarSpace;
  toggleDrawer: (open: boolean) => void;
}) => {
  const { highlightColor, setSplitPath } = useSplitScreenContext();

  const onSelect = (path: string) => {
    // 1. Ensure we start with a slash for consistent splitting
    const normalizedPath = path.startsWith("/") ? path : "/" + path;
    setSplitPath(normalizedPath);
    toggleDrawer(false);
  };

  const displayName = getSidebarDisplayName(link, space);

  return (
    <button
      onClick={() => onSelect(link.key)}
      className={cn(
        "w-full px-2 h-10 text-base font-semibold text-black border rounded-sxs text-start",
        `hover:bg-(--hover-bg) hover:text-white text-sm`,
        "flex items-center gap-2",
      )}
      style={
        {
          "--hover-bg": highlightColor,
        } as React.CSSProperties
      }
    >
      <span className="shrink-0 flex items-center justify-center w-6 h-6">
        <NavIcon src={link.icon} />
      </span>
      <span className="truncate">{displayName}</span>
    </button>
  );
};
